import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { SignOut, Power, Gear, DownloadSimple, MagnifyingGlass, ArrowLeft, X, Target } from "@phosphor-icons/react";
import { usePrismMode } from "../lib/prism-mode";
import { useAuth } from "../lib/auth";
import { station, structural, target as targetApi } from "../lib/api";
import { SettingsDialog } from "../components/SettingsDialog";
import { BubbleLevel } from "../components/BubbleLevel";
import { usePWAInstall } from "../hooks/usePWAInstall";

// ---------------------------------------------------------------------
// Screen Wake Lock — keeps the tablet from sleeping while the app is
// open. Uses the modern Wake Lock API where available (Chrome / Edge
// on Android + iPadOS 16.4+); the request is re-acquired whenever the
// document becomes visible again since the browser drops the lock on
// backgrounding. Silent no-op on unsupported browsers.
// ---------------------------------------------------------------------
function useScreenWakeLock() {
  const lockRef = useRef(null);
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.wakeLock) return;
    let cancelled = false;
    const acquire = async () => {
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) { try { await lock.release(); } catch { /* silent */ } return; }
        lockRef.current = lock;
        // If the OS releases it (e.g. tab backgrounded), null the ref
        // so the visibility handler knows to re-acquire on return.
        lock.addEventListener("release", () => { lockRef.current = null; });
      } catch { /* silent — permission denied, low battery mode, etc */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !lockRef.current) acquire();
    };
    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      const lock = lockRef.current;
      lockRef.current = null;
      if (lock) { try { lock.release(); } catch { /* silent */ } }
    };
  }, []);
}
import Dashboard from "./Dashboard";
import VerticalityCheck from "./VerticalityCheck";
import OvalityCheck from "./OvalityCheck";
import LevelCheck from "./LevelCheck";
import DiagonalCheck from "./DiagonalCheck";
import SetoutTool from "./SetoutTool";
import TemplateCut from "./TemplateCut";
import DomeCutout from "./DomeCutout";
import LiveSessions, { useLiveCount } from "./LiveSessions";
import Diagnostics from "./Diagnostics";

const WILSON_LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_leica-control/artifacts/u5m03uje_Wilson%20Design%202019.webp";
const FABAXIS_LOGO = "/fabaxis-logo.png";

// ------- Custom SVG tool icons (technical drafting style) --------------

const OvalityIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    <ellipse cx="32" cy="32" rx="26" ry="16" />
    <circle cx="32" cy="32" r="1.5" fill="currentColor" />
    <line x1="32" y1="16" x2="32" y2="10" strokeDasharray="2 2" />
    <line x1="32" y1="48" x2="32" y2="54" strokeDasharray="2 2" />
    <line x1="6" y1="32" x2="0" y2="32" strokeDasharray="2 2" />
    <line x1="58" y1="32" x2="64" y2="32" strokeDasharray="2 2" />
    <path d="M 4 40 L 8 32 L 4 24" />
    <path d="M 60 24 L 56 32 L 60 40" />
  </svg>
);

const VerticalityIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    <rect x="22" y="6" width="20" height="52" />
    <line x1="32" y1="4" x2="32" y2="62" strokeDasharray="3 3" opacity="0.5" />
    <circle cx="32" cy="60" r="2.5" fill="currentColor" />
    <path d="M 28 30 L 32 34 L 36 30" opacity="0.6" />
    <path d="M 28 44 L 32 48 L 36 44" opacity="0.6" />
  </svg>
);

const DiagonalIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    <rect x="6" y="12" width="52" height="40" />
    {/* Diagonal A-C — the "reference" one, solid + slightly thicker. */}
    <line x1="6" y1="12" x2="58" y2="52" strokeWidth="2.5" />
    {/* Diagonal B-D — the "does it match?" one, dashed to signal
        "under test / possibly off". This is the whole point of the
        tool: are the two diagonals equal? */}
    <line x1="58" y1="12" x2="6" y2="52" strokeDasharray="4 3" opacity="0.7" />
    {/* Corner pegs — solid dots. */}
    <circle cx="6" cy="12" r="2.5" fill="currentColor" />
    <circle cx="58" cy="12" r="2.5" fill="currentColor" />
    <circle cx="6" cy="52" r="2.5" fill="currentColor" />
    <circle cx="58" cy="52" r="2.5" fill="currentColor" />
    {/* Tiny "≠" glyph at the crossing to say "check equality". */}
    <line x1="30" y1="30" x2="34" y2="30" opacity="0.9" />
    <line x1="30" y1="34" x2="34" y2="34" opacity="0.9" />
  </svg>
);

// Level icon — a horizontal beam with two anchor pins (start/end),
// a design slope line and a couple of Z-delta stems dropping onto it.
const LevelIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    {/* beam body */}
    <line x1="6" y1="34" x2="58" y2="30" />
    {/* datum line (dashed) */}
    <line x1="6" y1="42" x2="58" y2="42" strokeDasharray="3 3" opacity="0.5" />
    {/* start anchor */}
    <circle cx="6" cy="34" r="3" fill="currentColor" />
    {/* end anchor */}
    <circle cx="58" cy="30" r="3" fill="currentColor" />
    {/* Δz stems */}
    <line x1="22" y1="33" x2="22" y2="42" opacity="0.7" />
    <circle cx="22" cy="33" r="2" fill="currentColor" opacity="0.9" />
    <line x1="40" y1="31" x2="40" y2="42" opacity="0.7" />
    <circle cx="40" cy="31" r="2" fill="currentColor" opacity="0.9" />
    {/* tolerance rail */}
    <line x1="6" y1="20" x2="58" y2="16" strokeDasharray="2 2" opacity="0.35" />
    <line x1="6" y1="48" x2="58" y2="44" strokeDasharray="2 2" opacity="0.35" />
  </svg>
);

const SpoolIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    <circle cx="32" cy="32" r="24" />
    <circle cx="32" cy="32" r="16" />
    <circle cx="32" cy="32" r="6" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      const x = 32 + Math.cos(rad) * 20;
      const y = 32 + Math.sin(rad) * 20;
      return <circle key={i} cx={x} cy={y} r="2" fill="currentColor" />;
    })}
  </svg>
);

// Set-out icon — a rectangular footprint with three surveyor pegs
// already planted (solid) and one target reticle showing where the
// laser is about to place the next peg. That "one still to do"
// dot at the fourth corner reads instantly as "layout in progress".
const SetoutIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    {/* Design outline of the shape being set out. */}
    <polygon points="10,14 54,14 54,50 10,50" strokeDasharray="3 3" opacity="0.55" />
    {/* Planted pegs — solid triangles at three corners. */}
    <polygon points="10,14 7,10 13,10" fill="currentColor" />
    <polygon points="54,14 51,10 57,10" fill="currentColor" />
    <polygon points="10,50 7,54 13,54" fill="currentColor" />
    {/* Target reticle at the fourth corner — where the laser is aiming next. */}
    <circle cx="54" cy="50" r="6" />
    <line x1="54" y1="42" x2="54" y2="46" />
    <line x1="54" y1="54" x2="54" y2="58" />
    <line x1="46" y1="50" x2="50" y2="50" />
    <line x1="58" y1="50" x2="62" y2="50" />
    <circle cx="54" cy="50" r="1.5" fill="currentColor" />
    {/* Laser ray from station (top-left) to the reticle. */}
    <line x1="6" y1="6" x2="52" y2="48" strokeDasharray="2 3" opacity="0.5" />
  </svg>
);

// Template icon — cross-section of a saddle joint: a big host pipe
// (viewed end-on) with a smaller pipe crossing it, showing the
// fishmouth cut curve as a bright saddle notch. Reads as "two tubes,
// one cope".
const TemplateIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    {/* Host tube — big circle, end-on. */}
    <circle cx="32" cy="40" r="22" />
    {/* Cut tube — smaller pipe crossing from top, drawn as two
        parallels. */}
    <line x1="22" y1="4" x2="22" y2="40" />
    <line x1="42" y1="4" x2="42" y2="40" />
    {/* The saddle notch itself — bright arc following the host's
        outer surface where the cut tube would land. */}
    <path
      d="M 22 40 Q 32 30 42 40"
      stroke="currentColor" strokeWidth="2.5" fill="none"
    />
    {/* Little tick marks showing "this is the cut edge". */}
    <line x1="22" y1="40" x2="20" y2="42" opacity="0.6" />
    <line x1="42" y1="40" x2="44" y2="42" opacity="0.6" />
    <line x1="32" y1="30" x2="32" y2="27" opacity="0.6" />
  </svg>
);

// Dome-end cutout icon — a dome cross-section with a small circle cut
// out of the shell. Reads instantly as "hemispherical head with hole".
const DomeIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    {/* Base ring line */}
    <line x1="8" y1="44" x2="56" y2="44" />
    {/* Dome shell — half arc rising from base to apex */}
    <path d="M 10 44 A 22 22 0 0 1 54 44" />
    {/* Cutout on the dome face — small circle */}
    <circle cx="24" cy="34" r="4" strokeWidth="2.2" />
    {/* Apex tick */}
    <line x1="32" y1="18" x2="32" y2="14" opacity="0.6" />
    {/* Base ticks */}
    <line x1="10" y1="44" x2="10" y2="48" opacity="0.6" />
    <line x1="54" y1="44" x2="54" y2="48" opacity="0.6" />
  </svg>
);

// Prism auto-shoot icon — a prism (triangle) with concentric target
// rings behind, hinting at "find and lock the prism".
const PrismIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    <circle cx="32" cy="32" r="26" opacity="0.35" />
    <circle cx="32" cy="32" r="18" opacity="0.55" />
    <circle cx="32" cy="32" r="10" opacity="0.8" />
    {/* Prism triangle at centre. */}
    <path d="M 32 20 L 44 40 L 20 40 Z" strokeWidth="2.5" />
    <line x1="32" y1="20" x2="32" y2="40" opacity="0.6" />
    {/* Corner brackets suggesting "aimed at" / "captured". */}
    <path d="M 4 4 L 12 4 M 4 4 L 4 12" strokeWidth="2.5" />
    <path d="M 60 4 L 52 4 M 60 4 L 60 12" strokeWidth="2.5" />
    <path d="M 4 60 L 12 60 M 4 60 L 4 52" strokeWidth="2.5" />
    <path d="M 60 60 L 52 60 M 60 60 L 60 52" strokeWidth="2.5" />
  </svg>
);

// Broadcast-tower style icon for the LIVE tile — three widening arcs
// radiating from a solid dot, borrowing the visual language of every
// "live stream" indicator ever put on a control panel.
const LiveIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
    <circle cx="32" cy="32" r="3" fill="currentColor" />
    <path d="M 22 22 A 14 14 0 0 1 42 22" />
    <path d="M 22 42 A 14 14 0 0 0 42 42" />
    <path d="M 14 14 A 25 25 0 0 1 50 14" opacity="0.7" />
    <path d="M 14 50 A 25 25 0 0 0 50 50" opacity="0.7" />
    <path d="M 6 6 A 36 36 0 0 1 58 6" opacity="0.4" />
    <path d="M 6 58 A 36 36 0 0 0 58 58" opacity="0.4" />
  </svg>
);

// Wrench + gear icon — signals "the admin box: connection, target, install, sign out"
const AdminIcon = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="32" cy="32" r="6" />
    <path d="M 32 12 v 6 M 32 46 v 6 M 12 32 h 6 M 46 32 h 6 M 18 18 l 4 4 M 42 42 l 4 4 M 46 18 l -4 4 M 22 42 l -4 4" />
    <circle cx="32" cy="32" r="18" strokeDasharray="3 3" opacity="0.5" />
  </svg>
);

// ------- Tool tile --------------------------------------------------------

const ToolTile = ({ id, label, sublabel, Icon, disabled, onClick, testid, beta }) => (
  <button
    data-testid={testid || `tool-${id}`}
    disabled={disabled}
    onClick={onClick}
    className={`group relative flex min-h-0 flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl border p-2 transition-all duration-150 sm:p-3 ${
      disabled
        ? "border-[#2A2E39] bg-[#14161A] opacity-40"
        : "border-[#3a3e49] bg-gradient-to-br from-[#2a2d35] to-[#23252c] shadow-[4px_4px_8px_#07080a,-4px_-4px_8px_#1c1f26] hover:border-[#009E8E]/50 hover:shadow-[0_0_15px_rgba(0,158,142,0.3),4px_4px_8px_#07080a,-4px_-4px_8px_#1c1f26] active:shadow-[inset_4px_4px_8px_#07080a,inset_-4px_-4px_8px_#1c1f26]"
    }`}
  >
    <div className="pointer-events-none absolute inset-1.5 border border-transparent">
      <div className="absolute left-0 top-0 h-2 w-2 border-l-2 border-t-2 border-[#009E8E]/40" />
      <div className="absolute right-0 top-0 h-2 w-2 border-r-2 border-t-2 border-[#009E8E]/40" />
      <div className="absolute bottom-0 left-0 h-2 w-2 border-b-2 border-l-2 border-[#009E8E]/40" />
      <div className="absolute bottom-0 right-0 h-2 w-2 border-b-2 border-r-2 border-[#009E8E]/40" />
    </div>
    {beta && (
      <span
        data-testid={`tool-${id}-beta-badge`}
        className="pointer-events-none absolute right-1.5 top-1.5 rounded-sm border border-[#F5A623]/70 bg-[#F5A623]/15 px-1 py-[1px] text-[8px] font-black uppercase leading-none tracking-[0.15em] text-[#F5A623] shadow-[0_0_6px_rgba(245,166,35,0.35)] sm:text-[9px] sm:tracking-[0.2em]"
        style={{ fontFamily: "Chakra Petch, sans-serif" }}
        title="Beta — field-testing in progress"
      >
        Beta
      </span>
    )}
    <Icon className="h-10 w-10 flex-none text-white transition-colors group-hover:text-[#009E8E] sm:h-12 sm:w-12" />
    <div className="text-center leading-tight">
      <div className="text-[11px] font-bold uppercase tracking-tight text-white sm:text-sm" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
        {label}
      </div>
      {sublabel && (
        <div className="mt-0.5 hidden text-[8px] uppercase tracking-[0.2em] text-[#8B939A] md:block" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          {sublabel}
        </div>
      )}
    </div>
  </button>
);

// Same shape as ToolTile, but tinted (teal accent) and never disabled by
// the TS16 offline gate — a supervisor watching a field surveyor doesn't
// need their own station connected. Shows an "N ACTIVE" badge if any
// sessions are heartbeating right now.
const LiveTile = ({ activeCount, onClick }) => (
  <button
    data-testid="tool-live"
    onClick={onClick}
    className="group relative flex min-h-0 flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl border border-[#00E1C7]/40 bg-gradient-to-br from-[#0E2A2A] to-[#0A1A1A] p-2 shadow-[4px_4px_8px_#07080a,-4px_-4px_8px_#122a2a] transition-all duration-150 hover:border-[#00E1C7] hover:shadow-[0_0_20px_rgba(0,225,199,0.4),4px_4px_8px_#07080a,-4px_-4px_8px_#122a2a] active:shadow-[inset_4px_4px_8px_#07080a,inset_-4px_-4px_8px_#122a2a] sm:p-3"
  >
    <div className="pointer-events-none absolute inset-1.5">
      <div className="absolute left-0 top-0 h-2 w-2 border-l-2 border-t-2 border-[#00E1C7]/60" />
      <div className="absolute right-0 top-0 h-2 w-2 border-r-2 border-t-2 border-[#00E1C7]/60" />
      <div className="absolute bottom-0 left-0 h-2 w-2 border-b-2 border-l-2 border-[#00E1C7]/60" />
      <div className="absolute bottom-0 right-0 h-2 w-2 border-b-2 border-r-2 border-[#00E1C7]/60" />
    </div>
    {activeCount > 0 && (
      <div className="absolute right-1.5 top-1.5 flex items-center gap-1 rounded-full border border-[#8DC63F] bg-[#8DC63F]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#B7E97A]">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#8DC63F] opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#8DC63F]" />
        </span>
        {activeCount}
      </div>
    )}
    <LiveIcon className="h-10 w-10 flex-none text-[#00E1C7] transition-colors group-hover:text-white sm:h-12 sm:w-12" />
    <div className="text-center leading-tight">
      <div className="text-[11px] font-bold uppercase tracking-tight text-white sm:text-sm" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
        Live
      </div>
      <div className="mt-0.5 hidden text-[8px] uppercase tracking-[0.2em] text-[#00E1C7] md:block" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        Watch active sessions
      </div>
    </div>
  </button>
);

// ------- Header + top status bar -----------------------------------------

/**
 * PrismLockPill — small live status chip that lives in the header
 * whenever Prism mode is active. Reflects the actual servo-lock state
 * of the TS16 (via `state.atr_locked` polled at 2 Hz).
 *
 *   LOCKED     — green solid, prism is being tracked
 *   SEARCHING  — amber pulsing, PowerSearch / Reacquire in progress
 *   LOST       — red, prism has fallen out of the ATR window
 *                Tap the pill to run `reacquire` (slew back to the
 *                last known good aim and re-run PowerSearch).
 */
/**
 * PrismLockPill — small live status chip that lives in the header
 * whenever Prism mode is active. Reflects the actual servo-lock state
 * of the TS16 (via `state.atr_locked` polled at 2 Hz).
 *
 *   LOCKED     — green solid, prism is being tracked
 *   SEARCHING  — amber pulsing, PowerSearch / Reacquire in progress.
 *                Tap to Stop.
 *   LOST       — red, prism was locked recently but is now gone.
 *                Tap to Reacquire (Stage-1 stare + Stage-2 home).
 *   IDLE       — grey, no lock, no session history. Tap to run a
 *                fresh Search from wherever the head is currently
 *                pointing.
 */
const PrismLockPill = ({ locked, searching, hasEverLocked, onReacquire, onStop, onSearch }) => {
  const state = searching ? "searching"
              : locked ? "locked"
              : hasEverLocked ? "lost"
              : "idle";
  const conf = {
    locked:    { color: "#8DC63F", bg: "#8DC63F26",  border: "#8DC63F", label: "Prism Lock",              pulse: false, clickable: false, onTap: undefined  },
    searching: { color: "#F5A623", bg: "#F5A62326", border: "#F5A623", label: "Searching · Tap to Stop", pulse: true,  clickable: true,  onTap: onStop     },
    lost:      { color: "#FF3B30", bg: "#FF3B3026", border: "#FF3B30", label: "Lock Lost · Reacquire",   pulse: false, clickable: true,  onTap: onReacquire },
    idle:      { color: "#00E1C7", bg: "#00E1C726", border: "#00E1C7", label: "Prism · Tap to Search",   pulse: false, clickable: true,  onTap: onSearch    },
  }[state];
  return (
    <button
      data-testid="header-prism-lock-pill"
      onClick={conf.clickable ? conf.onTap : undefined}
      disabled={!conf.clickable}
      title={
        state === "locked"    ? "TS is actively tracking your prism"
        : state === "searching" ? "TS is hunting for the prism — tap to cancel"
        : state === "lost"      ? "Lock lost — tap to slew back to last known aim and re-search"
        : "No lock yet — tap to run PowerSearch from current aim"
      }
      className={`flex min-h-[36px] items-center gap-1.5 rounded-md border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${conf.pulse ? "animate-pulse" : ""} ${conf.clickable ? "cursor-pointer" : "cursor-default"}`}
      style={{
        color: conf.color, background: conf.bg, borderColor: conf.border,
        fontFamily: "Chakra Petch, sans-serif",
      }}
    >
      <span className={`h-2 w-2 rounded-full`} style={{ background: conf.color, boxShadow: state === "locked" ? `0 0 8px ${conf.color}` : "none" }} />
      <span>{conf.label}</span>
    </button>
  );
};

/**
 * StartPointChip — visible "Start Point" state chip in the header,
 * mirroring how Automet FAB exposes its known-good aim location.
 *   * Tap when idle → captures the current Hz/V as the Start Point.
 *     Stage-2 recovery (Reacquire) uses this as its slew target.
 *   * Tap when set → slews the scope back to the Start Point without
 *     running a search (surveyor typically then puts the prism there
 *     for a quick manual re-lock).
 *   * Long-press when set → clears the Start Point.
 */
const StartPointChip = () => {
  const [hasHome, setHasHome] = useState(false);
  const pressTimerRef = useRef(null);
  const longPressedRef = useRef(false);

  // Poll home state at 1 Hz — cheap, and only when Prism mode is on
  // (component is conditionally mounted so this useEffect stops on
  // unmount anyway).
  useEffect(() => {
    let cancelled = false;
    let interval;
    (async () => {
      try {
        const s = await station.state();
        if (!cancelled) setHasHome(Number.isFinite(s?.hunt_home_hz));
      } catch { /* silent */ }
    })();
    interval = setInterval(async () => {
      try {
        const s = await station.state();
        if (!cancelled) setHasHome(Number.isFinite(s?.hunt_home_hz));
      } catch { /* silent */ }
    }, 1500);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const doSet = async () => {
    try {
      await station.setStartPoint();
      setHasHome(true);
      toast.success("Start Point locked to current aim");
    } catch (e) { toast.error(e?.response?.data?.detail || "Set Start Point failed"); }
  };
  const doReturn = async () => {
    try {
      await station.returnToStart();
      toast("Returning to Start Point…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Return failed"); }
  };
  const doClear = async () => {
    try {
      await station.clearStartPoint();
      setHasHome(false);
      toast("Start Point cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
  };

  const onPressStart = () => {
    longPressedRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      if (hasHome) doClear();
    }, 650);
  };
  const onPressEnd = () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    if (longPressedRef.current) return;    // long-press already handled
    if (hasHome) doReturn(); else doSet();
  };

  const color = hasHome ? "#8DC63F" : "#8B939A";
  return (
    <button
      data-testid="header-start-point-chip"
      onPointerDown={onPressStart}
      onPointerUp={onPressEnd}
      onPointerLeave={() => { if (pressTimerRef.current) clearTimeout(pressTimerRef.current); }}
      title={hasHome
        ? "Start Point is set — tap to return to it, long-press to clear"
        : "Tap to set the current aim as the Start Point (Stage-2 recovery target)"}
      className={`flex min-h-[36px] items-center gap-1.5 rounded-md border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${
        hasHome
          ? "border-[#8DC63F] bg-[#8DC63F]/15 text-[#8DC63F] hover:bg-[#8DC63F]/25"
          : "border-[#2A2E39] bg-[#14161A] text-[#8B939A] hover:border-[#8DC63F]/60 hover:text-[#8DC63F]"
      }`}
      style={{ fontFamily: "Chakra Petch, sans-serif" }}
    >
      <span className="h-2 w-2 rounded-full" style={{ background: color, boxShadow: hasHome ? `0 0 6px ${color}` : "none" }} />
      <span>{hasHome ? "Return to Start" : "Set Start Point"}</span>
    </button>
  );
};

/**
 * Header — minimal top bar. Only ever shows the FabAxis logo. When
 * `onBack` is provided (i.e. we're inside a tool), the logo itself
 * becomes the "go home" affordance. `onSignOut` renders a small
 * sign-out pill on the right — only used for the operator role
 * (admins sign out from the Admin panel). `onOpenLevel` renders a
 * bullseye icon that opens the on-tablet bubble-level widget so the
 * surveyor can true up the tripod without walking back to it.
 */
const Header = ({ onBack, onSignOut, state, onOpenLevel }) => {
  const connected = !!state?.connected;
  // Same tolerance thresholds the BubbleLevel widget uses — kept in
  // sync so the header icon's colour and the panel's status pill can
  // never disagree. RAD → arc-seconds: 1 rad = 206264.8″.
  const tiltMag = Math.hypot(state?.tilt_x || 0, state?.tilt_y || 0);
  const OK_INNER = 30 / 206264.806;   // 30″
  const OK_OUTER = 300 / 206264.806;  // 5′
  const levelColour =
    !connected     ? "#4A5054" :
    tiltMag < OK_INNER ? "#8DC63F" :
    tiltMag < OK_OUTER ? "#F5A623" : "#FF3B30";
  return (
  <header className="flex items-center justify-between gap-3 border-b border-[#2A2E39] bg-[#0A0B0E]/80 px-3 py-2 backdrop-blur-sm sm:px-4">
    <button
      data-testid="header-logo-btn"
      type="button"
      onClick={onBack}
      disabled={!onBack}
      title={onBack ? "Return to toolbox" : ""}
      className={`flex items-center gap-3 rounded-md ${onBack ? "hover:bg-[#14161A] cursor-pointer" : "cursor-default"}`}
    >
      <img src="/fabaxis-mark-light.png" alt="FabAxis" className="h-9 w-auto" data-testid="fabaxis-mark" />
      <div className="text-left">
        <div className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          <span className="text-[#B0000A]">Fab</span>Axis
        </div>
        <div className="hidden text-[9px] uppercase tracking-[0.25em] text-[#4A5054] sm:block">
          Field Controller
        </div>
      </div>
    </button>
    <div className="flex items-center gap-2">
      {/* Bubble-level icon — tap to open the full tilt widget. Border
          colour is a live level indicator so a surveyor 3 m from the
          tripod can tell at a glance whether it's still trued up. */}
      {onOpenLevel && (
        <button
          data-testid="header-level-btn"
          type="button"
          onClick={onOpenLevel}
          disabled={!connected}
          title={connected ? "Tap to view TS16 bubble level" : "Bridge offline"}
          className="flex min-h-[36px] items-center gap-1.5 rounded-md border bg-[#14161A] px-2 py-1.5 hover:bg-[#1E2128] disabled:cursor-not-allowed disabled:opacity-50"
          style={{ borderColor: levelColour }}
        >
          <Target size={16} weight="bold" style={{ color: levelColour }} />
          <span
            className="hidden font-mono text-[10px] uppercase tracking-widest sm:inline"
            style={{ color: levelColour }}
          >
            {!connected ? "—" : `${(tiltMag * 206264.806).toFixed(0)}″`}
          </span>
        </button>
      )}
      {/* TS16 connection pill — Green dot + "TS16 CONNECTED · 87%"
          (or plug icon + "EXT PWR" if running off a LEMO cable) when
          live, muted grey "OFFLINE" otherwise. Non-clickable — the
          surveyor's connection actions live in the Admin/Connect tile.

          state.battery = percentage 0-100 from CSV_CheckPower (RPC 5039).
          state.power_source: 0=unknown, 1=external LEMO, 2=internal
          GEB221. We hide the % if it's 0 (either not connected, or
          RPC unsupported by the firmware). */}
      <div
        data-testid="header-ts-status"
        className="flex min-h-[36px] items-center gap-2 rounded-md border border-[#2A2E39] bg-[#14161A] px-2.5 py-1.5 sm:px-3"
        title={connected ? `TS connected · battery ${Math.round(state?.battery || 0)}%` : "Bridge unreachable — open Admin → Settings"}
      >
        <div className={`h-2.5 w-2.5 rounded-full ${connected ? "bg-[#8DC63F] shadow-[0_0_8px_#8DC63F]" : "bg-[#4A5054]"}`} />
        <span className="text-[10px] uppercase tracking-widest text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          TS16 · {connected ? "CONNECTED" : "OFFLINE"}
        </span>
        {connected && (state?.power_source === 1) && (
          <span
            data-testid="header-ext-power"
            className="hidden items-center gap-1 font-mono text-[11px] text-[#00E1C7] sm:inline-flex"
            title="Running off external LEMO cable"
          >
            <span className="text-base leading-none">⚡</span>EXT
          </span>
        )}
        {connected && (state?.power_source !== 1) && Number.isFinite(state?.battery) && state.battery > 0 && (
          <span
            data-testid="header-battery-pct"
            className={`hidden font-mono text-[11px] sm:inline ${
              state.battery <= 15 ? "text-[#FF3B30]"
              : state.battery <= 30 ? "text-[#F5A623]"
              : "text-white"
            }`}
            title={`Internal battery ${Math.round(state.battery)}%`}
          >
            {Math.round(state.battery)}%
          </span>
        )}
      </div>
      {onSignOut && (
        <button
          data-testid="header-signout-btn"
          onClick={onSignOut}
          className="flex min-h-[36px] items-center gap-1.5 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#FF3B30] hover:text-[#FF3B30]"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          <SignOut size={13} weight="bold" /> Sign out
        </button>
      )}
    </div>
  </header>
  );
};

/**
 * InstrumentSetupCard — DynamicLock toggle, AutoHeight readback/edit,
 * and the entry-point button for Free Station / Resection. Lives in
 * AdminPanel so operators can access it once per setup and forget.
 */
const InstrumentSetupCard = () => {
  const [dynLock, setDynLock] = useState(false);
  const [dynLockPending, setDynLockPending] = useState(false);
  const [height, setHeight] = useState(null);   // metres, null=unread
  const [heightEdit, setHeightEdit] = useState("");
  const [heightPending, setHeightPending] = useState(false);
  const [freeStationOpen, setFreeStationOpen] = useState(false);
  useEffect(() => {
    // Read stored tribrach height once when the panel mounts.
    station.getInstrumentHeight()
      .then((d) => { setHeight(d.height_m); setHeightEdit(d.height_m.toFixed(3)); })
      .catch(() => {});
  }, []);
  const toggleDynLock = async () => {
    setDynLockPending(true);
    try {
      const r = await station.dynamicLock(!dynLock);
      setDynLock(!!r.on);
      toast[r.acknowledged ? "success" : "warning"](
        r.acknowledged ? `DynamicLock ${r.on ? "ON" : "OFF"}` : "Firmware doesn't expose DynamicLock RPC — no-op"
      );
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
    finally { setDynLockPending(false); }
  };
  const saveHeight = async () => {
    const h = parseFloat(heightEdit);
    if (!isFinite(h) || h < 0.5 || h > 3) { toast.error("Height must be 0.5-3.0 m"); return; }
    setHeightPending(true);
    try { await station.setInstrumentHeight(h); setHeight(h); toast.success(`Height set to ${h.toFixed(3)} m`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Set failed"); }
    finally { setHeightPending(false); }
  };
  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A]"
           style={{ fontFamily: "Chakra Petch, sans-serif" }}>
        Instrument setup
      </div>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* DynamicLock */}
        <button
          data-testid="admin-dynlock-toggle" onClick={toggleDynLock} disabled={dynLockPending}
          className={`flex min-h-[52px] flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left ${dynLock
            ? "border-[#00E1C7] bg-[#00E1C7]/10 text-[#00E1C7]"
            : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A] hover:border-[#00E1C7]/50 hover:text-[#00E1C7]"}`}
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          <span className="text-[10px] uppercase tracking-widest">DynamicLock</span>
          <span className="text-[9px] uppercase tracking-widest opacity-75">
            {dynLock ? "prediction ON" : "prediction OFF"}
          </span>
        </button>
        {/* AutoHeight */}
        <div className="flex min-h-[52px] flex-col justify-center gap-1 rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-1.5"
             style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">Tribrach height (m)</div>
          <div className="flex items-center gap-2">
            <input
              data-testid="admin-height-input" type="number" step="0.001" min="0.5" max="3.0"
              value={heightEdit} onChange={(e) => setHeightEdit(e.target.value)}
              className="min-h-[28px] w-24 rounded border border-[#2A2E39] bg-[#14161A] px-2 py-1 text-right font-mono text-sm text-white"
            />
            <button
              data-testid="admin-height-save" onClick={saveHeight} disabled={heightPending}
              className="rounded border border-[#8DC63F]/60 bg-[#8DC63F]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#8DC63F] hover:bg-[#8DC63F]/20 disabled:opacity-40"
            >
              Set
            </button>
          </div>
        </div>
        {/* Free Station */}
        <button
          data-testid="admin-freestation-btn" onClick={() => setFreeStationOpen(true)}
          className="flex min-h-[52px] flex-col items-start gap-0.5 rounded-md border border-[#F5A623]/50 bg-[#F5A623]/5 px-3 py-2 text-left text-[#F5A623] hover:bg-[#F5A623]/10"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          <span className="text-[10px] uppercase tracking-widest">Free Station</span>
          <span className="text-[9px] uppercase tracking-widest opacity-75">Resection · 2+ backsights</span>
        </button>
      </div>
      <FreeStationModal open={freeStationOpen} onClose={() => setFreeStationOpen(false)} />
    </div>
  );
};

/**
 * ReflectorlessShotCard — shown when the target chip is set to
 * Reflectorless. Big "TAKE RL SHOT" button, latest Hz/V/SD result
 * shown below.
 */
const ReflectorlessShotCard = () => {
  const [pending, setPending] = useState(false);
  const [last, setLast] = useState(null);
  const shoot = async () => {
    setPending(true);
    try {
      const r = await station.measureReflectorless();
      setLast(r);
      toast.success(`RL shot: SD ${r.sd.toFixed(3)} m`);
    } catch (e) { toast.error(e?.response?.data?.detail || "RL shot failed"); }
    finally { setPending(false); }
  };
  return (
    <div className="rounded-md border border-[#F5A623]/40 bg-[#F5A623]/5 p-3">
      <div className="text-[9px] uppercase tracking-[0.3em] text-[#F5A623]"
           style={{ fontFamily: "Chakra Petch, sans-serif" }}>
        Reflectorless shot
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button
          data-testid="admin-rl-shot" onClick={shoot} disabled={pending}
          className="min-h-[44px] rounded-md border border-[#F5A623] bg-[#F5A623]/20 px-4 py-2 text-[12px] font-black uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/30 disabled:opacity-40"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}
        >
          {pending ? "Measuring…" : "Take RL shot"}
        </button>
        {last && (
          <div className="font-mono text-[10px] text-[#B5BEC5]">
            Hz {(last.hz * 180 / Math.PI).toFixed(3)}° · V {(last.v * 180 / Math.PI).toFixed(3)}° · SD {last.sd.toFixed(3)} m
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * FreeStationModal — 2+ backsight capture flow. For each backsight,
 * the surveyor enters known XYZ and taps "Capture Hz/V/SD" (which
 * reads the current TS state — they've aimed via the ATR at the
 * known prism location). When ≥2 sights are captured, they hit
 * "Solve" → we POST to /resection/solve → show station XYZ +
 * per-sight residuals in mm.
 */
const FreeStationModal = ({ open, onClose }) => {
  const [sights, setSights] = useState([]);
  const [fix, setFix] = useState(null);
  const [pending, setPending] = useState(false);
  useEffect(() => { if (open) { setSights([]); setFix(null); } }, [open]);
  const captureCurrent = async () => {
    const name = prompt("Control point name (e.g. CP1)?");
    if (!name) return;
    const known_x = parseFloat(prompt("Known X (m)?") || "");
    const known_y = parseFloat(prompt("Known Y (m)?") || "");
    const known_z = parseFloat(prompt("Known Z (m)?") || "");
    if (![known_x, known_y, known_z].every(isFinite)) { toast.error("XYZ required"); return; }
    try {
      const s = await import("../lib/api").then((m) => m.api.get("/station/state").then((r) => r.data));
      const sd = parseFloat(prompt(`SD to ${name} (m)? — take a distance measurement on the TS keypad first`) || "");
      if (!isFinite(sd) || sd <= 0) { toast.error("SD required"); return; }
      setSights((prev) => [...prev, {
        name, known_x, known_y, known_z,
        hz_rad: s.hz, v_rad: s.v, sd_m: sd,
      }]);
      toast.success(`Captured ${name}`);
    } catch (e) { toast.error("Failed to read state"); }
  };
  const solve = async () => {
    setPending(true);
    try {
      const r = await import("../lib/api").then((m) => m.resection.solve(sights));
      setFix(r);
      toast[r.ok ? "success" : "warning"](
        r.ok ? `Fit OK — worst residual ${r.worst_mm.toFixed(1)} mm`
             : `Fit poor — worst residual ${r.worst_mm.toFixed(1)} mm (>30 mm)`
      );
    } catch (e) { toast.error(e?.response?.data?.detail || "Solve failed"); }
    finally { setPending(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-[#0A0B0E]/90 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-md border border-[#F5A623]/50 bg-[#14161A] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div className="text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            Free Station · Resection
          </div>
          <button data-testid="free-station-close" onClick={onClose}
            className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#FF3B30] hover:text-[#FF3B30]">
            Close
          </button>
        </div>
        <div className="mb-3 rounded border border-dashed border-[#2A2E39] p-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
          Aim at each known control point, take a distance shot on the TS keypad, then tap Capture. Repeat for ≥ 2 points.
        </div>
        <button data-testid="fs-capture-btn" onClick={captureCurrent}
          className="mb-3 min-h-[40px] w-full rounded-md border border-[#00E1C7] bg-[#00E1C7]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#00E1C7] hover:bg-[#00E1C7]/20"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          + Capture current backsight
        </button>
        <div className="mb-3 space-y-1">
          {sights.map((s, i) => (
            <div key={i} className="rounded border border-[#2A2E39] bg-[#0A0B0E] p-2 font-mono text-[10px] text-[#B5BEC5]">
              <span className="text-[#00E1C7]">{s.name}</span> — known ({s.known_x.toFixed(2)}, {s.known_y.toFixed(2)}, {s.known_z.toFixed(2)}) · Hz {(s.hz_rad * 180 / Math.PI).toFixed(3)}° · V {(s.v_rad * 180 / Math.PI).toFixed(3)}° · SD {s.sd_m.toFixed(3)} m
            </div>
          ))}
          {sights.length === 0 && <div className="text-[10px] uppercase tracking-widest text-[#4A5054]">No backsights yet</div>}
        </div>
        <button data-testid="fs-solve-btn" onClick={solve} disabled={pending || sights.length < 2}
          className="min-h-[44px] w-full rounded-md border-2 border-[#F5A623] bg-[#F5A623]/15 px-4 py-2 text-[13px] font-black uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/25 disabled:opacity-40"
          style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          {pending ? "Solving…" : `Solve station (${sights.length} sights)`}
        </button>
        {fix && (
          <div className="mt-4 rounded border border-[#2A2E39] bg-[#0A0B0E] p-3 font-mono text-[11px]">
            <div className={`text-[12px] font-bold uppercase tracking-widest ${fix.ok ? "text-[#8DC63F]" : "text-[#FF3B30]"}`}>
              {fix.ok ? "FIT OK" : "FIT POOR"} · worst {fix.worst_mm.toFixed(1)} mm (tol 30 mm)
            </div>
            <div className="mt-2 text-[#B5BEC5]">
              Station: ({fix.x.toFixed(4)}, {fix.y.toFixed(4)}, {fix.z.toFixed(4)}) m
            </div>
            <div className="text-[#B5BEC5]">Hz offset: {(fix.hz_offset_rad * 180 / Math.PI).toFixed(4)}°</div>
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Per-sight residuals:</div>
            {fix.residuals_mm.map((r, i) => (
              <div key={i} className={r > 30 ? "text-[#FF3B30]" : "text-[#8DC63F]"}>
                {sights[i]?.name}: {r.toFixed(2)} mm
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * LevelModal — full-screen tap-away dialog holding the BubbleLevel
 * widget. Opened from the header bullseye icon so the surveyor can
 * spot-check the tripod without opening the whole Admin panel or
 * walking back to the tripod. Full-screen on tablet, tap outside to
 * dismiss.
 */
const LevelModal = ({ open, onClose, state }) => {
  if (!open) return null;
  const connected = !!state?.connected;
  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-[#0A0B0E]/90 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="level-modal"
    >
      <div
        className="w-full max-w-md rounded-md border border-[#2A2E39] bg-[#14161A] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              TS16
            </div>
            <div className="text-xl font-bold uppercase tracking-tight"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Bubble level
            </div>
          </div>
          <button
            data-testid="level-modal-close" onClick={onClose}
            className="flex min-h-[40px] items-center gap-1.5 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#FF3B30] hover:text-[#FF3B30]"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            <X size={14} weight="bold" /> Close
          </button>
        </div>
        <div className="flex justify-center">
          <BubbleLevel
            tiltX={state?.tilt_x || 0}
            tiltY={state?.tilt_y || 0}
            connected={connected}
            size={280}
          />
        </div>
        <div className="mt-4 rounded-md border border-dashed border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-center text-[10px] uppercase tracking-widest text-[#8B939A]"
             style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Inner ring — within 30″ · Outer ring — within 5′
        </div>
      </div>
    </div>
  );
};

/**
 * AdminPanel — full-screen dialog holding every setting/status/action
 * that used to live in the top bar: TS16 connection + battery, Prism /
 * Reflectorless toggle, Prism-Lock pill, Start Point chip, Install to
 * home-screen, TS Diagnostics, Settings, and Sign out. Opened from the
 * new Admin tile on the toolbox home page.
 */
const AdminPanel = ({ open, onClose, user, state, onLogout, onOpenSettings, onOpenDiag }) => {
  const [prismMode, setPrismMode] = usePrismMode();
  const isAdmin = user?.role === "admin";
  const connected = !!state?.connected;
  const { canInstall, isStandalone, isIos, install } = usePWAInstall();
  const hasEverLockedRef = useRef(false);
  if (state?.atr_locked && !hasEverLockedRef.current) hasEverLockedRef.current = true;

  const togglePrismMode = async () => {
    const nextOn = !prismMode;
    setPrismMode(nextOn);
    try {
      if (nextOn) {
        const savedId = localStorage.getItem("fabaxis.lastPrismPresetId") || "ball_25_4";
        const updated = await targetApi.set({ preset_id: savedId });
        const picked = updated?.preset_id === "custom" ? updated.custom : null;
        if (picked && Number.isInteger(picked.bap_target_type)) {
          station.setTargetType(picked.bap_target_type).catch(() => undefined);
        } else {
          station.setTargetType(0).catch(() => undefined);
        }
        toast.success("Prism mode — TS set to prism target");
      } else {
        try {
          const cur = await targetApi.get();
          if (cur?.preset_id && cur.preset_id !== "rl") {
            localStorage.setItem("fabaxis.lastPrismPresetId", cur.preset_id);
          }
        } catch { /* silent */ }
        await targetApi.set({ preset_id: "rl" });
        station.setTargetType(1).catch(() => undefined);
        toast.success("Reflectorless mode — laser on surface");
      }
    } catch (e) {
      toast.error("Could not update target on TS");
    }
  };

  const handleInstall = async () => {
    if (canInstall) {
      const r = await install();
      if (r.ok) toast.success("FabAxis installed to home screen");
      return;
    }
    if (isIos) {
      toast("Install on iPad / iPhone", {
        description: "Tap the Share button in Safari, then choose 'Add to Home Screen'.",
        duration: 8000,
      });
      return;
    }
    toast("Install FabAxis", {
      description: "Open this page in Chrome, then use the browser menu → 'Install app' / 'Add to Home screen'.",
      duration: 8000,
    });
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex flex-col overflow-y-auto bg-[#0A0B0E]/95 backdrop-blur-md" data-testid="admin-panel">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 sm:p-6">
        {/* Panel header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              {isAdmin ? "Admin" : "Connect"}
            </div>
            <div className="text-xl font-bold uppercase tracking-tight sm:text-2xl"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              {isAdmin ? "Status & settings" : "Total station"}
            </div>
          </div>
          <button
            data-testid="admin-panel-close" onClick={onClose}
            className="flex min-h-[40px] items-center gap-1.5 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#FF3B30] hover:text-[#FF3B30]"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            <X size={14} weight="bold" /> Close
          </button>
        </div>

        {/* TS16 connection + battery */}
        <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A]"
               style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            Total Station
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <div className="flex min-h-[36px] items-center gap-2 rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-1.5">
              <div className={`h-2.5 w-2.5 rounded-full ${connected ? "bg-[#8DC63F] shadow-[0_0_8px_#8DC63F]" : "bg-[#4A5054]"}`} />
              <span className="text-[10px] uppercase tracking-widest text-[#8B939A]"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}>
                TS16 · {connected ? "CONNECTED" : "OFFLINE"}
              </span>
              {connected && state?.power_source === 1 && (
                <span className="pl-2 font-mono text-[11px] text-[#00E1C7]" title="External LEMO power">⚡ EXT</span>
              )}
              {connected && state?.power_source !== 1 && Number.isFinite(state?.battery) && state.battery > 0 && (
                <span className={`pl-2 font-mono text-[11px] ${
                  state.battery <= 15 ? "text-[#FF3B30]"
                  : state.battery <= 30 ? "text-[#F5A623]"
                  : "text-white"
                }`}>
                  {Math.round(state.battery)}%
                </span>
              )}
            </div>
            {state?.ts_tracking && (
              <div className="flex min-h-[36px] animate-pulse items-center gap-1.5 rounded-md border border-[#00E1C7] bg-[#00E1C7]/15 px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#00E1C7] shadow-[0_0_12px_rgba(0,225,199,0.35)]"
                   style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <span className="h-2 w-2 rounded-full bg-[#00E1C7] shadow-[0_0_6px_#00E1C7]" />
                TS Tracking Active
              </div>
            )}
          </div>
        </div>

        {/* On-tablet TS16 bubble level — surveyor can true up the
            tripod without walking back to it. Reads live `tilt_x/tilt_y`
            from state (populated by the bridge poll from TMC_GetAngle5
            RPC 2107, cross+length inclination in radians). */}
        <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A]"
               style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            Bubble level
          </div>
          <div className="mt-2 flex justify-center">
            <BubbleLevel
              tiltX={state?.tilt_x || 0}
              tiltY={state?.tilt_y || 0}
              connected={connected}
              size={220}
            />
          </div>
        </div>

        {/* Target mode + Lock controls */}
        {connected && (
          <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A]"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Target mode
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                data-testid="admin-prism-toggle"
                onClick={togglePrismMode}
                className={`flex min-h-[40px] items-center gap-1.5 rounded-md border px-3 py-2 text-[11px] font-bold uppercase tracking-widest ${prismMode
                  ? "border-[#8DC63F] bg-[#8DC63F]/15 text-[#8DC63F] hover:bg-[#8DC63F]/25"
                  : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A] hover:border-[#8DC63F]/60 hover:text-[#8DC63F]"}`}
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <span className="text-[13px] leading-none">◈</span>
                <span>{prismMode ? "Prism" : "Reflectorless"}</span>
              </button>
              {prismMode && (
                <PrismLockPill
                  locked={!!state?.atr_locked}
                  searching={!!state?.atr_searching}
                  hasEverLocked={hasEverLockedRef.current}
                  onSearch={async () => {
                    try { await station.powerSearch(); toast.success("Prism locked — move to your first point"); }
                    catch (e) { toast.error(e?.response?.data?.detail || "No prism found"); }
                  }}
                  onReacquire={async () => {
                    try { await station.atr("reacquire"); toast.success("Reacquired — tracking prism"); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Reacquire failed"); }
                  }}
                  onStop={async () => {
                    try { await station.atr("stop"); toast("Search cancelled"); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Stop failed — is your bridge up to date?"); }
                  }}
                />
              )}
              {prismMode && <StartPointChip />}
            </div>
          </div>
        )}

        {/* Instrument setup — DynamicLock + AutoHeight + Free Station.
            Grouped in one card so the surveyor's setup checklist is one
            place. All rows collapse gracefully when disconnected. */}
        {connected && <InstrumentSetupCard />}

        {/* Reflectorless single shot — surveyor aims telescope at a
            steel/concrete face, taps, gets Hz/V/SD. Prism mode is
            restored afterwards. Only shown in Reflectorless target
            mode so it doesn't confuse the prism-only workflow. */}
        {connected && !prismMode && <ReflectorlessShotCard />}

        {/* Tools & install */}
        <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A]"
               style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            Actions
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {!isStandalone && (
              <button
                data-testid="admin-install-btn" onClick={handleInstall}
                className="flex min-h-[40px] items-center gap-1.5 rounded-md border border-[#009E8E]/60 bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#8DEDDF] hover:border-[#009E8E] hover:bg-[#009E8E]/20"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <DownloadSimple size={14} weight="bold" /> Install to home screen
              </button>
            )}
            <button
              data-testid="admin-settings-btn"
              onClick={() => { onClose(); onOpenSettings(); }}
              className="flex min-h-[40px] items-center gap-1.5 rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <Gear size={14} weight="bold" /> Settings & bridge
            </button>
            {onOpenDiag && connected && isAdmin && (
              <button
                data-testid="admin-diag-btn"
                onClick={() => { onClose(); onOpenDiag(); }}
                className="flex min-h-[40px] items-center gap-1.5 rounded-md border border-[#00E1C7] bg-[#00E1C7]/15 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#00E1C7] hover:bg-[#00E1C7]/30"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <MagnifyingGlass size={14} weight="bold" /> TS Diagnostics
              </button>
            )}
          </div>
        </div>

        {/* User + logout — admin only. Operators sign out from the
            top-right header pill; keeping this hidden avoids two
            different sign-out paths for the same user role. */}
        {isAdmin && (
          <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#8B939A]"
                 style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Signed in as
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <div className="text-[13px] font-bold uppercase tracking-widest text-white"
                   style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {user?.username || "—"}
              </div>
              <button
                data-testid="admin-logout-btn" onClick={onLogout}
                className="flex min-h-[40px] items-center gap-1.5 rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#8B939A] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <SignOut size={14} weight="bold" /> Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ------- The toolbox page -----------------------------------------------

export default function SurveyToolbox() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  useScreenWakeLock();
  const [state, setState] = useState(null);
  const [activeTool, setActiveTool] = useState(null);   // null | "spool" | "ovality" | "vert" | "diag" | "live"
  const [showSettings, setShowSettings] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showLevel, setShowLevel] = useState(false);
  const liveCount = useLiveCount();

  // Poll station state at 2 Hz so the connection LED is live.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await station.state();
        if (!cancelled) setState({ ...s, connected: !!s });
      } catch {
        if (!cancelled) setState((prev) => (prev ? { ...prev, connected: false } : null));
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const connected = !!state?.connected;

  // Route into the live-viewer page — used by supervisor accounts
  // watching a field surveyor. Doesn't require TS16 to be connected.
  if (activeTool === "live") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="live-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <LiveSessions onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  // TS Diagnostics — one-tap debug panel that fires known RPC
  // sequences and shows exact return codes. For sharing with the
  // developer when Search/Lock/Track misbehave.
  if (activeTool === "tsdiag") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="tsdiag-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <Diagnostics onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  const openTool = (id) => {
    // "Live" is a supervisor watch page — doesn't need the station.
    if (id === "live") { setActiveTool("live"); return; }
    if (!connected) {
      toast.error("Connect the TS16 first — bridge.py must be running");
      return;
    }
    if (id === "spool") { setActiveTool("spool"); return; }
    if (id === "vert") { setActiveTool("vert"); return; }
    if (id === "ovality") { setActiveTool("oval"); return; }
    if (id === "level") { setActiveTool("level"); return; }
    if (id === "diag") { setActiveTool("diagonal"); return; }
    if (id === "setout") { setActiveTool("setout"); return; }
    if (id === "template") { setActiveTool("template"); return; }
    if (id === "dome") { setActiveTool("dome"); return; }
    // Any tile that isn't yet built.
    toast(`${id.toUpperCase()} — coming next update`, {
      description: "This tool's UI is being finalized.",
      duration: 4000,
    });
  };

  // Spool = the existing Dashboard's spool tab. Instead of duplicating the
  // UI we route into the original Dashboard component in "spool" mode.
  if (activeTool === "spool") {
    return (
      <div>
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <div className="flex items-center justify-between border-b border-[#2A2E39] bg-[#14161A] px-4 py-2">
          <div className="text-sm uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            Spool Survey
          </div>
          <button
            data-testid="tool-back-btn"
            onClick={() => setActiveTool(null)}
            className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] px-3 py-1 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
          >
            ← Toolbox
          </button>
        </div>
        <Dashboard forceTab="spool" hideHeader />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "vert") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="verticality-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <VerticalityCheck onBack={() => setActiveTool(null)} />
        {/* Wilson chrome footer badge — consistent branding across tools */}
        <footer className="mt-8 border-t border-[#1A1D24] py-6">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-2 px-4">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#4A5054]">
              FabAxis · Field Controller · v2.0
            </div>
            <div
              data-testid="wilson-chrome-logo-vert"
              className="h-7 w-28"
              style={{
                maskImage: `url(${WILSON_LOGO})`,
                WebkitMaskImage: `url(${WILSON_LOGO})`,
                maskSize: "contain",
                WebkitMaskSize: "contain",
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
                background: "linear-gradient(to bottom, #ffffff 0%, #a6aeb5 35%, #5a6065 50%, #8b939a 55%, #e2e5e8 100%)",
                filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.8))",
              }}
            />
          </div>
        </footer>
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "oval") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="ovality-page">
        <div className="no-print">
          <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        </div>
        <OvalityCheck onBack={() => setActiveTool(null)} />
        <footer className="mt-8 border-t border-[#1A1D24] py-6 no-print">
          <div className="mx-auto flex max-w-5xl flex-col items-center gap-2 px-4">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#4A5054]">
              FabAxis · Field Controller · v2.0
            </div>
            <div
              data-testid="wilson-chrome-logo-oval"
              className="h-7 w-28"
              style={{
                maskImage: `url(${WILSON_LOGO})`,
                WebkitMaskImage: `url(${WILSON_LOGO})`,
                maskSize: "contain",
                WebkitMaskSize: "contain",
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
                background: "linear-gradient(to bottom, #ffffff 0%, #a6aeb5 35%, #5a6065 50%, #8b939a 55%, #e2e5e8 100%)",
                filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.8))",
              }}
            />
          </div>
        </footer>
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "level") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="level-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <LevelCheck onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "diagonal") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="diagonal-tool-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <DiagonalCheck onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "setout") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="setout-tool-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <SetoutTool onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "template") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="template-tool-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <TemplateCut onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  if (activeTool === "dome") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="dome-tool-page">
        <Header onBack={() => setActiveTool(null)} state={state} onOpenLevel={() => setShowLevel(true)} />
        <DomeCutout onBack={() => setActiveTool(null)} />
        <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#0A0B0E] text-white" data-testid="toolbox-page">
      <Header onSignOut={!isAdmin ? logout : undefined} state={state} />

      <main className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col p-3 sm:p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Survey Toolbox</div>
            <div className="text-lg font-bold uppercase tracking-tight sm:text-xl" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Choose an inspection
            </div>
          </div>
          {!connected && (
            <div className="flex items-center gap-2 rounded-md border border-[#FF9F0A] bg-[#FF9F0A]/10 px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#FF9F0A]">
              <Power size={12} weight="bold" /> Connect the TS16 to enable tools
            </div>
          )}
        </div>

        {/* All tiles fit in the viewport without scrolling. Grid
            column count depends on how many tiles this user sees:
              - admin (10 tiles) → 5 × 2 on tablet landscape
              - operator (8 tiles) → 4 × 2 on tablet landscape
            Degrades gracefully to 3 columns on portrait tablet and
            2 on phone. Tiles auto-size to the available height via
            `flex-1` + row minmax so a shorter viewport just shrinks
            them; nothing scrolls off. */}
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-5 md:grid-rows-2">
          <ToolTile
            id="ovality"
            testid="tool-ovality"
            label="Ovality & Flatness"
            sublabel="Round-section + plane fit"
            Icon={OvalityIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("ovality")}
          />
          <ToolTile
            id="verticality"
            testid="tool-verticality"
            label="Verticality"
            sublabel="Plumb along member"
            Icon={VerticalityIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("vert")}
          />
          <ToolTile
            id="level"
            testid="tool-level"
            label="Level"
            sublabel="Slope or flat surface"
            Icon={LevelIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("level")}
          />
          <ToolTile
            id="diagonal"
            testid="tool-diagonal"
            label="Diagonal"
            sublabel="Rectangle squareness"
            Icon={DiagonalIcon}
            disabled={!connected}
            onClick={() => openTool("diag")}
          />
          <ToolTile
            id="spool"
            testid="tool-spool"
            label="Spool"
            sublabel="ANSI / DIN flange"
            Icon={SpoolIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("spool")}
          />
          <ToolTile
            id="setout"
            testid="tool-setout"
            label="Set-out"
            sublabel="Shape → ground points"
            Icon={SetoutIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("setout")}
          />
          <ToolTile
            id="template"
            testid="tool-template"
            label="Template"
            sublabel="Saddle-cut layout"
            Icon={TemplateIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("template")}
          />
          <ToolTile
            id="dome"
            testid="tool-dome"
            label="Dome"
            sublabel="End cutout set-out"
            Icon={DomeIcon}
            disabled={!connected}
            beta
            onClick={() => openTool("dome")}
          />
          {isAdmin && (
            <LiveTile
              activeCount={liveCount}
              onClick={() => openTool("live")}
            />
          )}
          <ToolTile
            id="admin"
            testid="tool-admin"
            label={isAdmin ? "Admin" : "Connect"}
            sublabel={isAdmin ? "Status · Target · Settings" : "Bridge · Target"}
            Icon={AdminIcon}
            onClick={() => setShowAdmin(true)}
          />
        </div>

        <div className="mt-6 rounded-md border border-[#2A2E39] bg-[#14161A] p-4 text-[11px] text-[#8B939A]">
          <b className="text-white">Getting started</b> — Pair the Leica TS16 with this tablet over Bluetooth, then start the bridge (bridge.py) on the tablet or a paired laptop. The TS16 · CONNECTED light will go green as soon as the bridge is up. Then any tool tile above is one tap away.
        </div>
      </main>

      {/* Wilson chrome footer badge */}
      <footer className="mt-8 border-t border-[#1A1D24] py-6">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-2 px-4">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#4A5054]">
            FabAxis · Field Controller · v2.0
          </div>
          <div
            data-testid="wilson-chrome-logo"
            className="h-7 w-28"
            style={{
              maskImage: `url(${WILSON_LOGO})`,
              WebkitMaskImage: `url(${WILSON_LOGO})`,
              maskSize: "contain",
              WebkitMaskSize: "contain",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskPosition: "center",
              background: "linear-gradient(to bottom, #ffffff 0%, #a6aeb5 35%, #5a6065 50%, #8b939a 55%, #e2e5e8 100%)",
              filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.8))",
            }}
          />
        </div>
      </footer>
      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} state={state} />
        <LevelModal open={showLevel} onClose={() => setShowLevel(false)} state={state} />
      <AdminPanel
        open={showAdmin}
        onClose={() => setShowAdmin(false)}
        user={user}
        state={state}
        onLogout={logout}
        onOpenSettings={() => setShowSettings(true)}
        onOpenDiag={() => setActiveTool("tsdiag")}
      />
    </div>
  );
}
